import { debounce } from 'lodash-es';
import React, { useMemo } from 'react';

import { SearchIcon } from '../icons/SearchIcon';

export interface SearchTypeaheadProps {
  value?: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  placeholder?: string;
  hint?: string;
  dataTestId?: string;
  adornment?: React.ReactNode;
  minLength?: number;
}

export const SearchTypeahead = React.forwardRef<
  HTMLInputElement,
  SearchTypeaheadProps
>(
  (
    {
      value,
      onChange,
      onEnter,
      placeholder,
      hint,
      dataTestId,
      adornment,
      minLength,
    },
    ref
  ) => {
    const handleReportValidity = useMemo(
      () =>
        debounce((target: HTMLInputElement) => {
          target.reportValidity();
        }, 750),
      []
    );

    return (
      <div>
        <div className="relative isolate overflow-hidden">
          <SearchIcon />
          <input
            ref={ref}
            className="block w-full appearance-none rounded-md bg-transparent py-1 pl-9 pr-4 text-base text-slate-900 placeholder:text-slate-600 focus:outline-none sm:text-sm sm:leading-6"
            placeholder={placeholder}
            aria-label={placeholder}
            type="text"
            style={{ caretColor: 'rgb(107, 114, 128)' }}
            minLength={minLength}
            onChange={(e) => {
              onChange(e.target.value);

              // Trigger browsers validation reporter
              if (minLength) {
                handleReportValidity(e.target);
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                onEnter?.();
              } else if (e.key === 'Escape') {
                e.currentTarget.blur();
              }
            }}
            value={value}
            data-testid={dataTestId}
          />
          {adornment && (
            <span
              data-testid={`${dataTestId}-adornment`}
              className="absolute right-2 top-2 text-sm text-gray-500"
            >
              {adornment}
            </span>
          )}
          <span className="text-xs text-gray-500">{hint}</span>
        </div>
      </div>
    );
  }
);
